// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import Component from '../../entities/components/Component';
import AnimationLayer from '../../animationpack/layer/AnimationLayer';
import JointData from '../../animationpack/clip/JointData';
import TransformData from '../../animationpack/clip/TransformData';
import TriggerData from '../../animationpack/clip/TriggerData';

/**
 * Holds the animation data.
 */
class AnimationComponent extends Component {

  /**
   * @param {SkeletonPose} pose pose
   */
  constructor(pose) {
    super();

    this.type = 'AnimationComponent';

    /**
     * @type {Array<AnimationLayer>}
     */
    this.layers = [];
    this.floats = {};

    this._updateRate = 1.0 / 60.0;
    this._lastUpdate = 0.0;
    this._triggerCallbacks = {};

    // Base layer
    const layer = new AnimationLayer(AnimationLayer.BASE_LAYER_NAME, undefined, this.entity ? this.entity.world : null);
    this.layers.push(layer);
    this._skeletonPose = pose;

    this.paused = false;
    this.lastTimeOfPause = -1;
  }

  /**
   * Transition to another state. This is shorthand for applying transitions on the state layer provided, if not, transitions will be applied on the  base layer , see {@link AnimationLayer.transitionTo} for more info
   * @param {string} stateKey
   * @param {boolean} allowDirectSwitch Allow the function to directly switch state if transitioning fails (missing or transition already in progress)
   * @param {Function} callback If the target state has a limited number of repeats, this callback is called when the animation finishes.
   * @returns {boolean} true if a transition was found and started
   */
  transitionTo(stateKey, allowDirectSwitch, callback) {
    const layer = this.getLayerByStateId(stateKey);
    if (layer) {
      return layer.transitionTo(stateKey, undefined, callback, allowDirectSwitch);
    }
    console.log('[Sumerian] The state provided for this entity' + this.entity.id + ' does not exist. Transition failed.');
  }

  /**
   * Get the state by id from any layer.
   * @param {string} id
   * @returns {AbstractState}
   */
  getStateById(id) {
    let state;
    for (let layer of this.layers) {
      if (state = layer.getStateById(id)) {
        return state;
      }
    }
    console.log('[Sumerian] The state provided for this entity' + this.entity.id + ' does not exist.');
  }

  /**
   * Get the layer by id containing a particular state.
   * @param {string} stateId
   * @returns {AnimationLayer}
   */
  getLayerByStateId(stateId) {
    for (let layer of this.layers) {
      if (layer.getStateById(stateId)) {
        return layer;
      }
    }
    console.log('[Sumerian] The state provided for this entity' + this.entity.id + '  does not exist.');
  }

  /**
   * Get available states from all the existing layers.
   * returns {Array<string>} available state keys
   */
  getStates() {
    let states = [];
    for (let layer of this.layers) {
       states.push.apply(states, layer.getStates());
    }
    return states;
  }

  /**
   * Get current state to which the animationComponent has transitioned.
   * returns {AbstractState}
   */
  getCurrentState() {
    let currentState;
    for (let layer of this.layers) {
      if (currentState = layer.getCurrentState()) {
        return currentState;
      }
    }
  }

  /**
   * Get available transitions
   * returns {Array<string>} available state keys
   */
  getTransitions() {
    let allTransitions = [];
    for (let layer of this.layers) {
       allTransitions.push(layer.getTransitions());
    }
    return allTransitions;
  }

  /**
   * Update animations
   */
  update(globalTime) {
    if (this.paused) {
      return;
    }

    // grab current global time
    globalTime = typeof globalTime !== 'undefined' ? globalTime : this.entity.world.time;

    // check throttle
    if (this._updateRate !== 0.0) {
      if (globalTime > this._lastUpdate && globalTime - this._lastUpdate < this._updateRate) {
        return;
      }

      // we subtract a bit to maintain our desired rate, even if there are some gc pauses, etc.
      this._lastUpdate = globalTime - (globalTime - this._lastUpdate) % this._updateRate;
    }

    // move the time forward on the layers
    for (let i = 0, max = this.layers.length; i < max; i++) {
      this.layers[i].update(globalTime);
    }
  }

  /*
   * Applying calculated animations to the concerned data
   */
  apply(transformComponent) {
    const data = this.getCurrentSourceData();
    if (!data) { return; }

    const pose = this._skeletonPose;

    // cycle through, pulling out and applying those we know about
    const keys = Object.keys(data);
    for (var i = 0, l = keys.length; i < l; i++) {
      const key = keys[i];
      const value = data[key];
      if (value instanceof JointData) {
        // Note: _jointIndex === undefined indicates the channel value does not map to a joint on
        // the local skeleton and can therefore be ignored
        if (pose && value._jointIndex >= 0) {
          value.applyTo(pose._localTransforms[value._jointIndex]);
        }
      } else if (value instanceof TransformData) {
        if (transformComponent) {
          value.applyTo(transformComponent.transform);
          transformComponent.setUpdated();
        }
      } else if (value instanceof TriggerData) {
        if (value.armed) {
          // pull callback(s) for the current trigger key, if exists, and call.
          // TODO: Integrate with GameMaker somehow
          for (var i = 0, maxI = value._currentTriggers.length; i < maxI; i++) {
            const callbacks = this._triggerCallbacks[value._currentTriggers[i]];
            if (callbacks && callbacks.length) {
              for (let j = 0, maxJ = callbacks.length; j < maxJ; j++) {
                callbacks[j]();
              }
            }
          }
          value.armed = false;
        }
      } else if (value instanceof Array) {
        this.floats[key] = value[0];
      }
    }
    if (pose) {
      pose.updateTransforms();
    }
  }

  /*
   * Called after the animations are applied
   */
  postUpdate() {
    // post update to clear states
    for (let i = 0, max = this.layers.length; i < max; i++) {
      this.layers[i].postUpdate();
    }
  }

  /*
   * Gets the current animation data for all layers blended together
   */
  getCurrentSourceData() {
    // set up our layer blending.
    if (this.layers.length === 0) {
      return [];
    }
    const last = this.layers.length - 1;
    this.layers[0]._layerBlender = null;
    for (let i = 0; i < last; i++) {
      this.layers[i + 1].updateLayerBlending(this.layers[i]);
    }
    return this.layers[last].getCurrentSourceData();
  }

  /**
   * Add a new {@link AnimationLayer} to the stack
   * @param {AnimationLayer} layer
   * @param {number} [index] if no index is supplied, it's put on top of the stack
   */
  addLayer(layer, index) {
    layer.world = this.entity.world;
    if (!isNaN(index)) {
      this.layers.splice(index, 0, layer);
    } else {
      this.layers.push(layer);
    }
  }

  resetClips(globalTime) {
    for (let i = 0; i < this.layers.length; i++) {
      this.layers[i].resetClips(globalTime);
    }
  }

  shiftClipTime(shiftTime) {
    for (let i = 0; i < this.layers.length; i++) {
      this.layers[i].shiftClipTime(shiftTime);
    }
  }

  setTimeScale(timeScale) {
    for (let i = 0; i < this.layers.length; i++) {
      this.layers[i].setTimeScale(timeScale);
    }
  }

  pause() {
    if (!this.paused) {
      this.lastTimeOfPause = this.entity.world.time;
      this.paused = true;
    }
  }

  stop() {
    if (this._skeletonPose) {
      this._skeletonPose.setToBindPose();
    }
    this.paused = true;
    this.lastTimeOfPause = -1;
  }

  resume() {
    if (this.paused || this.lastTimeOfPause === -1) {
      if (this.lastTimeOfPause === -1) {
        this.resetClips(this.entity.world.time);
      } else {
        this.shiftClipTime(this.entity.world.time - this.lastTimeOfPause);
      }
    }
    this.paused = false;
  }

  /**
   * Called when the component was added to an entity. The default implementation
   * sets the components entity property to the provided value.
   *
   * @param {Entity} entity The entity being attached.
   */
  attached(entity) {
    super.attached(entity);
    this.layers.forEach((layer) => {
      layer.world = this.entity.world;
    });
  }

  /**
   * Called when the component was removed from an entity. The default
   * implementation sets the component's entity property to null.
   *
   * @param {Entity} entity The entity being detached.
   */
  detached(entity) {
    super.detached(entity);
    this.layers.forEach((layer) => {
      layer.world = null;
    });
  }

  clone() {
    const cloned = new AnimationComponent();

    cloned.layers = this.layers.map(layer => layer.clone());
    return cloned;
  }
}

AnimationComponent.type = 'AnimationComponent';

export default AnimationComponent;
